import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { toast } from "sonner";
import type { SessionDetail, SessionSummary } from "../../../../shared/api.ts";
import { Button } from "@/components/ui/button.tsx";
import { ConfirmDialog } from "@/components/ui/confirm-dialog.tsx";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Label } from "@/components/ui/label.tsx";
import { useI18n } from "@/i18n/index.tsx";
import { api, queryKeys } from "@/lib/api.ts";
import { errorMessage } from "@/lib/hooks.ts";

/** `POST /api/sessions`, then open it. */
export function useCreateSession() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { t } = useI18n();
  return useMutation({
    mutationFn: () => api.createSession(),
    onSuccess: (session) => {
      queryClient.setQueryData<SessionSummary[]>(queryKeys.sessions, (list) => [session, ...(list ?? []).filter((item) => item.id !== session.id)]);
      void navigate(`/app/s/${session.id}`);
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });
}

export function RenameSessionDialog({
  session,
  open,
  onOpenChange,
}: {
  session: Pick<SessionSummary, "id" | "title">;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState(session.title);
  useEffect(() => {
    if (open) setTitle(session.title);
  }, [open, session.title]);

  const rename = useMutation({
    mutationFn: (next: string) => api.renameSession(session.id, { title: next }),
    onSuccess: (_data, next) => {
      queryClient.setQueryData<SessionSummary[]>(queryKeys.sessions, (list) =>
        list?.map((item) => (item.id === session.id ? { ...item, title: next } : item)),
      );
      queryClient.setQueryData<SessionDetail>(queryKeys.session(session.id), (detail) =>
        detail ? { ...detail, session: { ...detail.session, title: next } } : detail,
      );
      void queryClient.invalidateQueries({ queryKey: queryKeys.sessions });
      toast.success(t.shell.renamed);
      onOpenChange(false);
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });

  const trimmed = title.trim();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t.common.close} className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t.shell.renameTitle}</DialogTitle>
        </DialogHeader>
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (trimmed && trimmed !== session.title) rename.mutate(trimmed);
            else onOpenChange(false);
          }}
        >
          <div className="flex flex-col gap-2">
            <Label htmlFor="rename-session">{t.shell.renameLabel}</Label>
            <Input id="rename-session" value={title} maxLength={200} autoFocus onChange={(event) => setTitle(event.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t.common.cancel}
            </Button>
            <Button type="submit" disabled={!trimmed || rename.isPending}>
              {rename.isPending ? t.common.saving : t.common.save}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteSessionDialog({
  session,
  open,
  onOpenChange,
}: {
  session: Pick<SessionSummary, "id" | "title">;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const remove = useMutation({
    mutationFn: () => api.deleteSession(session.id),
    onSuccess: () => {
      queryClient.setQueryData<SessionSummary[]>(queryKeys.sessions, (list) => list?.filter((item) => item.id !== session.id));
      queryClient.removeQueries({ queryKey: queryKeys.session(session.id) });
      toast.success(t.shell.deleted);
      onOpenChange(false);
      if (location.pathname === `/app/s/${session.id}`) void navigate("/app", { replace: true });
    },
    onError: (error) => toast.error(errorMessage(error, t)),
  });
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t.shell.deleteTitle}
      description={t.shell.deleteText(session.title || t.shell.untitled)}
      confirmLabel={remove.isPending ? t.common.deleting : t.common.delete}
      cancelLabel={t.common.cancel}
      pending={remove.isPending}
      onConfirm={() => remove.mutate()}
    />
  );
}
